import React, { useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { injectIntl } from "react-intl";
import { gql, useQuery } from "@apollo/client";
import { styled } from "@material-ui/styles";
import { Grid, Theme } from "@material-ui/core";
import AddIcon from "@material-ui/icons/AddRounded";
import { PaperLayout } from "../atoms";
import Content from "../content";
import Button from "../button";
import Typography from "../typography";
import Breadcrumb from "../page-view-team/Breadcrumb";
import { IStyledArguments } from "../../types/styled-arguments";
import CreatePreSeasonExerciseDialog from "./CreatePreSeasonExerciseDialog";
import PreSeasonExerciseList from "./PreSeasonExerciseList";
import PreSeasonProgressTable from "./PreSeasonProgressTable";
import PreSeasonSummaryTable from "./PreSeasonSummaryTable";
import messages from "./PageTeamPreSeason.intl";

export const TEAM_PRE_SEASON = gql`
  query TeamPreSeason($teamId: ID!) {
    team(id: $teamId) {
      id
      name
      squad {
        id
        name
        surname
        avatarUrl
      }
    }
    teamPreSeasonExercises(teamId: $teamId) {
      id
      title
      description
      type
      startDate
      endDate
      weekdays
      totalEntries
      completedEntries
      notDoneEntries
      players {
        id
      }
    }
  }
`;

const MainContent = styled(Content)<Theme>(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const HeaderRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const ViewToggleRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

type PageTeamPreSeasonProps = { id: string } & Required<
  Pick<RouteComponentProps, "navigate" | "path">
>;

const PageTeamPreSeason = injectIntl<PageTeamPreSeasonProps>(
  ({ id, intl }) => {
    const { data, refetch } = useQuery(TEAM_PRE_SEASON, {
      variables: { teamId: id },
    });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedExerciseId, setSelectedExerciseId] = useState<
      string | undefined
    >();
    const [viewMode, setViewMode] = useState<"summary" | "matrix">("summary");

    const team = data && data.team;
    const exercises = (data && data.teamPreSeasonExercises) || [];

    return (
      <PaperLayout>
        <MainContent container>
          <Grid item xs={12}>
            {team && <Breadcrumb team={team} />}
            <HeaderRow>
              <div>
                <Typography.H5>
                  {intl.formatMessage(messages.title)}
                </Typography.H5>
                <Typography.Subtitle1 style={{ color: "#666" }}>
                  {intl.formatMessage(messages.subtitle)}
                </Typography.Subtitle1>
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setDialogOpen(true)}
              >
                <AddIcon style={{ marginRight: 6 }} fontSize="small" />
                {intl.formatMessage(messages.newExercise)}
              </Button>
            </HeaderRow>

            {exercises.length === 0 && (
              <Typography.Subtitle1 style={{ color: "#666" }}>
                {intl.formatMessage(messages.noExercises)}
              </Typography.Subtitle1>
            )}

            {exercises.length > 0 && (
              <PreSeasonExerciseList
                exercises={exercises}
                selectedId={selectedExerciseId}
                onSelect={setSelectedExerciseId}
                onDeleted={() => {
                  setSelectedExerciseId(undefined);
                  refetch();
                }}
              />
            )}

            {!selectedExerciseId && exercises.length > 0 && (
              <Typography.Subtitle1 style={{ color: "#999", marginTop: 16 }}>
                {intl.formatMessage(messages.selectExercisePrompt)}
              </Typography.Subtitle1>
            )}

            {selectedExerciseId && (
              <div style={{ marginTop: 24 }}>
                <ViewToggleRow>
                  <Button
                    variant={viewMode === "summary" ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => setViewMode("summary")}
                  >
                    {intl.formatMessage(messages.viewSummary)}
                  </Button>
                  <Button
                    variant={viewMode === "matrix" ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => setViewMode("matrix")}
                  >
                    {intl.formatMessage(messages.viewMatrix)}
                  </Button>
                </ViewToggleRow>
                {viewMode === "summary" ? (
                  <PreSeasonSummaryTable exerciseId={selectedExerciseId} />
                ) : (
                  <PreSeasonProgressTable exerciseId={selectedExerciseId} />
                )}
              </div>
            )}
          </Grid>
        </MainContent>

        {team && (
          <CreatePreSeasonExerciseDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            teamId={id}
            squad={team.squad || []}
            onCreated={() => {
              setDialogOpen(false);
              refetch();
            }}
          />
        )}
      </PaperLayout>
    );
  }
);

export default PageTeamPreSeason;
